import type { Reports } from './reports.type.ts';
import type {
  SearchConfig,
  SearchConfigFilters,
  ReportMetadata,
  ReportScope,
  ChartMetadata,
  ChartSettings,
  TableSettings,
  AggregationTableSettings,
  AggregationMetric,
  RangeBucket,
  PartnerAccessConfig,
} from './reports.type.ts';

export type {
  SearchConfig,
  SearchConfigFilters,
  ReportMetadata,
  ReportScope,
  ChartMetadata,
  ChartSettings,
  TableSettings,
  AggregationTableSettings,
  AggregationMetric,
  RangeBucket,
  PartnerAccessConfig,
};
export interface ReportsExtension extends Reports {}
export class ReportsExtension {
  // Computed properties
  get isPrivate(): boolean {
    return this.visibilityLevel === 'private';
  }

  get isPartnerShared(): boolean {
    return this.visibilityLevel === 'partner';
  }

  get isGlobal(): boolean {
    return this.visibilityLevel === 'global';
  }

  get isChartBuilder(): boolean {
    return this.reportType === 'chart_builder';
  }

  get isTableBuilder(): boolean {
    return this.reportType === 'table_builder';
  }

  get isAggregationTable(): boolean {
    return this.reportType === 'aggregation_table';
  }

  get isDashboard(): boolean {
    return this.reportType === 'dashboard';
  }

  // Scope (published state) properties
  get scope(): ReportScope {
    return this.metadata?.scope === 'partner_admin' ? 'partner_admin' : 'admin_only';
  }

  get isPublished(): boolean {
    return this.scope === 'partner_admin';
  }

  get isUnpublished(): boolean {
    return !this.isPublished;
  }

  get publishedAt(): string | undefined {
    return this.metadata?.publishedAt;
  }

  get publishedCompanyId(): string | undefined {
    return this.metadata?.publishedCompanyId;
  }

  /**
   * PARTNER-channel state only. Use `publishChannelsLabel` (tio-reporting
   * utils/publish-channels) anywhere a report may also be published to the
   * internal channel — this getter calls such a report "Unpublished".
   */
  get scopeLabel(): string {
    return this.isPublished ? 'Published' : 'Unpublished';
  }

  // The API omits these fields for non-internal viewers.
  get isInternalPublished(): boolean {
    return Boolean(this.internalPublishedAt);
  }

  get isSqlBacked(): boolean {
    return Boolean(this.sqlBacked);
  }

  get isArchived(): boolean {
    return Boolean(this.metadata?.archivedAt);
  }

  get archivedAt(): string | undefined {
    return this.metadata?.archivedAt;
  }

  // Partner access is resolved server-side; visibleToAllPartners,
  // partnerIncludeList and partnerExcludeList are served fields. Deriving them
  // here meant reimplementing the server's reader, which only ever handled one
  // of the two stored shapes of partnerAccessConfig.
  get visibilityDescription(): string {
    if (this.isGlobal) return 'Visible to all users';
    if (this.isPrivate) return 'Private (only you)';
    if (!this.isPartnerShared) return 'Unknown visibility';

    const excludeCount = this.partnerExcludeList?.length ?? 0;
    if (excludeCount > 0) {
      return `Visible to all partners except ${excludeCount}`;
    }

    if (this.visibleToAllPartners) return 'Visible to all partners';

    const includeCount = this.partnerIncludeList?.length ?? 0;
    if (includeCount > 0) {
      return `Visible to ${includeCount} specific partner${includeCount === 1 ? '' : 's'}`;
    }

    // Partner-shared with nothing granted: the API serves this to no one.
    return 'Visible to no partners';
  }

  get hasSerializedQuery(): boolean {
    return Boolean(this.metadata?.serializedQuery);
  }

  get serializedQuery(): string | undefined {
    return this.metadata?.serializedQuery;
  }

  get displayName(): string {
    return this.name;
  }

  get formattedCreatedAt(): string {
    if (!this.createdAt) return '';
    return new Date(this.createdAt).toLocaleDateString();
  }

  get formattedUpdatedAt(): string {
    if (!this.updatedAt) return '';
    return new Date(this.updatedAt).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  get visibilityLabel(): string {
    switch (this.visibilityLevel) {
      case 'private':
        return 'Private';
      case 'partner':
        return 'Custom Partner Report';
      case 'global':
        return 'Global';
      default:
        return 'Unknown';
    }
  }

  get reportTypeLabel(): string {
    switch (this.reportType) {
      case 'chart_builder':
        return 'Chart Builder';
      case 'table_builder':
        return 'Table Builder';
      case 'aggregation_table':
        return 'Aggregation Table';
      case 'dashboard':
        return 'Dashboard';
      default:
        return 'Unknown';
    }
  }
}

const Registration = {
  kind: 'object',
  name: 'report',
  features: ReportsExtension,
};
export default Registration;
